import { Injectable } from '@angular/core';

import { environment } from 'src/environments/environment';

export interface Option {
  origin: string;
  path?: string;
  storage?: string;
}

// Salvaguarda: tempo máximo de espera por uma resposta do storage
// (iframe cross-domain). Se o iframe não responder nesse prazo, o getItem
// resolve com o valor em cache para não pendurar guards/login indefinidamente.
const GET_TIMEOUT_MS = 8000;

// Mesmo racional para escritas: o postMessage já foi enviado e o cache
// síncrono (asyncValues) já reflete o valor — se o iframe não confirmar no
// prazo, resolve como não-confirmado (false) em vez de deixar a Promise
// pendurada em quem estiver aguardando (ex.: rastreador de tentativas).
const SET_TIMEOUT_MS = 8000;

interface Message {
  id: number;
  origin: string;
  type: 'set' | 'get' | 'remove';
  key: string;
  resolve: any;
  reject: any;
  value: any;
  storage?: string;
}

interface IStorage {
  getItem(message: Message): void;
  setItem(message: Message): void;
  removeItem(message: Message): void;
  // Descarta uma mensagem que expirou sem resposta: sem isso, cada operação
  // não respondida (iframe morto) vaza a entrada no map/fila para sempre.
  evict(message: Message): void;
}

class IFrameStorage implements IStorage {
  private iframe: any | null = null;
  private iframeReady = false;
  private queue: Message[] = [];
  private messages: { [key: number]: Message } = {};

  constructor(private option: Option) {
    this.init();
  }

  private init(): void {
    if (!this.iframe) {
      this.iframe = document.createElement('iframe');
      this.iframe.style.cssText = 'position:absolute;width:1px;height:1px;left:-9999px;';
      document.body.appendChild(this.iframe);

      if (window.addEventListener) {
        this.iframe.addEventListener('load', () => this.iframeLoaded(), false);
        window.addEventListener('message', event => this.handleMessage(event), false);
      } else if (this.iframe.attachEvent) {
        this.iframe.attachEvent('onload', () => this.iframeLoaded(), false);
        window['attachEvent']('onmessage', event => this.handleMessage(event));
      }

      this.iframe.src = `${this.option.origin}${this.option.path}`;
    }
  }

  private sendMessage(message: Message): void {
    if (this.iframe && this.iframe.contentWindow) {
      this.messages[message.id] = message;

      const data = JSON.stringify({
        id: message.id,
        type: message.type,
        key: message.key,
        storage: message.storage,
        value: message.value,
      });

      this.iframe.contentWindow.postMessage(data, this.option.origin);
    }
  }

  private handleMessage(event: MessageEvent): void {
    if (event.origin === this.option.origin) {
      try {
        const data = JSON.parse(event.data);
        const message = this.messages[data.id];

        if (!!data && !!message && data.key === message.key) {
          if (message.type === 'get') {
            message.resolve(JSON.parse(data.value));
          } else if (['set', 'remove'].includes(message.type) && data.value) {
            message.resolve();
          } else {
            message.reject();
          }
        }

        delete this.messages[data.id];
      } catch (error) {
        console.log(event);
      }
    }
  }

  private iframeLoaded(): void {
    this.iframeReady = true;
    this.queue.forEach(item => this.sendMessage(item));
    this.queue = [];
  }

  public getItem(message: Message): void {
    if (this.iframeReady) {
      this.sendMessage(message);
    } else {
      this.queue.push(message);
    }
  }

  public setItem(message: Message): void {
    if (this.iframeReady) {
      this.sendMessage(message);
    } else {
      this.queue.push(message);
    }
  }

  public removeItem(message: Message): void {
    if (this.iframeReady) {
      this.sendMessage(message);
    } else {
      this.queue.push(message);
    }
  }

  public evict(message: Message): void {
    delete this.messages[message.id];

    const queued = this.queue.indexOf(message);
    if (queued >= 0) {
      this.queue.splice(queued, 1);
    }
  }
}

class NativeStorage implements IStorage {

  constructor(private option: Option) { }

  // window[storage] pode ser null em browsers que bloqueiam acesso
  // (modo privado, política corporativa, etc). Antes causava
  // TypeError fatal no getItem da inicialização (auth/unidade guards).
  private get nativeStorage(): Storage | null {
    return (window[this.option.storage] as Storage) || null;
  }

  public getItem(message: Message): void {
    const storage = this.nativeStorage;
    if (!storage) {
      message.resolve(null);
      return;
    }
    const data = storage.getItem(message.key);
    message.resolve(JSON.parse(data));
  }

  public setItem(message: Message): void {
    const storage = this.nativeStorage;
    if (!storage) {
      message.resolve();
      return;
    }
    storage.setItem(message.key, message.value);
    message.resolve();
  }

  public removeItem(message: Message): void {
    const storage = this.nativeStorage;
    if (!storage) {
      message.resolve();
      return;
    }
    storage.removeItem(message.key);
    message.resolve();
  }

  public evict(): void {
    // storage síncrono não enfileira mensagens — nada a descartar
  }
}

@Injectable()
export class CrossDomainStorageService {

  LAST_ACTION_KEY = 'last_action';

  private option: Option;
  private idCount: number;
  private storage: IStorage;
  private asyncValues: { [key: string]: any } = {};

  constructor() {
    this.option = {
      origin: environment.CrossDomainStorageOrigin,
      path: '/cross-domain-storage/',
      storage: 'localStorage'
    };

    this.idCount = 0;
    if (environment['electron'] || !this.supported() || !this.option.origin) {
      this.storage = new NativeStorage(this.option);
    } else {
      this.storage = new IFrameStorage(this.option);
    }
  }

  private supported(): boolean {
    return window['JSON'] && this.option.storage in window && window[this.option.storage];
  }

  public updateLastUserAction(): void {
    this.setItem(this.LAST_ACTION_KEY, Date.now());
  }

  public preLoad(keys: string[]): Promise<any> {
    const list = keys.map(key => this.getItem(key));
    return Promise.all(list);
  }

  public getItemAsync(key: string): any {
    return this.asyncValues[key];
  }

  public getItem<T>(key: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      let settled = false;

      const message: Message = {
        type: 'get',
        id: ++this.idCount,
        origin: this.option.origin,
        storage: this.option.storage,
        resolve: null,
        reject: null,
        key: key,
        value: null,
      };

      // Se o storage não responder no prazo, resolve com o valor já em cache
      // (ex.: o token gravado de forma síncrona no login) em vez de pendurar;
      // a mensagem sem resposta é descartada para não vazar no IFrameStorage.
      const timer = setTimeout(() => {
        if (!settled) {
          settled = true;
          this.storage.evict(message);
          resolve(this.asyncValues[key] ?? null);
        }
      }, GET_TIMEOUT_MS);

      message.resolve = (value: any) => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          resolve(value);
        }
      };
      message.reject = (err: any) => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          reject(err);
        }
      };

      this.storage.getItem(message);
    }).then(item => {
      this.asyncValues[key] = item;
      return Promise.resolve(item);
    });
  }

  public setItem<T>(key: string, value: T): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const message: Message = {
        type: 'set',
        id: ++this.idCount,
        origin: this.option.origin,
        storage: this.option.storage,
        resolve: null,
        reject: null,
        key: key,
        value: JSON.stringify(value),
      };

      const settle = this.settleWithTimeout(resolve, reject, () => this.storage.evict(message));
      message.resolve = settle.resolve;
      message.reject = settle.reject;

      this.storage.setItem(message);
      this.asyncValues[key] = value;
    });
  }

  public removeItem(key: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const message: Message = {
        type: 'remove',
        id: ++this.idCount,
        origin: this.option.origin,
        storage: this.option.storage,
        resolve: null,
        reject: null,
        key: key,
        value: null,
      };

      const settle = this.settleWithTimeout(resolve, reject, () => this.storage.evict(message));
      message.resolve = settle.resolve;
      message.reject = settle.reject;

      this.storage.removeItem(message);
      delete this.asyncValues[key];
    });
  }

  // Envolve o resolve/reject de uma escrita com a salvaguarda de tempo: se o
  // iframe não confirmar em SET_TIMEOUT_MS, resolve(false) — nunca deixa a
  // Promise pendurada (o valor síncrono em asyncValues já foi atualizado).
  // onTimeout descarta a mensagem sem resposta do IFrameStorage (evita leak).
  private settleWithTimeout(
    resolve: (confirmed: boolean) => void,
    reject: (err: any) => void,
    onTimeout: () => void
  ): { resolve: () => void; reject: (err: any) => void } {
    let settled = false;

    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        onTimeout();
        resolve(false);
      }
    }, SET_TIMEOUT_MS);

    return {
      resolve: () => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          resolve(true);
        }
      },
      reject: (err: any) => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          reject(err);
        }
      },
    };
  }
}
